import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useRef, useState, type FormEvent } from "react";
import { optionalString } from "@/lib/search";
import { startSignupInput } from "@alm/validation";
import { ExistingAccountPanel } from "@/components/app/onboarding/existing-account-panel";
import { AuthFrame } from "@/components/marketing/auth-frame";
import { Button } from "@/components/ui/button";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { signInWithLoginKey } from "@/lib/host/auth-client";
import { writeLastActivity } from "@/lib/session/idle-timeout";
import { productHostRedirect } from "@/lib/marketing/login-host";
import { marketingHead } from "@/lib/marketing/seo";
import { orpcPublicClient } from "@/lib/orpc";

export const Route = createFileRoute("/signup")({
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: optionalString(search.email),
  }),
  /* The product's door only opens on the product's host: the session it ends in is a cookie for
   * assisted-care.com, and the sign-in behind it refuses an origin it does not trust. Served for
   * ppl-ai.com, the page sends the visitor to the same address there, query and all. */
  beforeLoad: ({ location }) => {
    const elsewhere = productHostRedirect(location);
    if (elsewhere) throw redirect({ href: elsewhere, replace: true });
  },
  head: () =>
    marketingHead({
      title: "Sign up for People AI",
      description:
        "Create your People AI account with your work email: we email you a code, then you choose a password.",
      path: "/signup",
    }),
  component: SignupPage,
});

/** Self-serve signup: work email, then the emailed code, the credential. Verifying creates the
 * account or resumes a run; the browser trades the key for a session and lands on the first run. An
 * address with a login gets no key and goes to sign in. */
function SignupPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [email, setEmail] = useState(search.email ?? "");
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  /* The code found a login that already exists: the person says whether they
     meant to sign in (with that login's own password) or to start afresh. */
  const [existing, setExisting] = useState<{
    next: string;
    role: string | null;
    portalHome: string | null;
  } | null>(null);
  // Off the tab order and out of sight: a browser never fills it, a bot does.
  const [__hp, setHp] = useState("");

  const start = useMutation({
    mutationFn: (address: string) =>
      orpcPublicClient.onboarding.signup.start({ email: address, __hp }),
    onSuccess: (_result, address) => {
      setSentTo(address);
      setCode("");
      setProblem(null);
    },
    onError: (error: Error) => {
      setProblem(error.message || "Couldn't send the code. Check the address and try again.");
      emailRef.current?.focus();
    },
  });

  const signIn = useMutation({
    mutationFn: async (session: { key: string; next: string }) => {
      const signedIn = await signInWithLoginKey(session.key);
      if (!signedIn) {
        throw new Error("Sign-in did not complete. Ask for a new code and try again.");
      }
      return signedIn.next ?? session.next;
    },
    onSuccess: async (next) => {
      writeLastActivity(Date.now());
      await navigate({ href: next, replace: true });
    },
    onError: (error: Error) => setProblem(error.message || "That code was not accepted."),
  });

  const verify = useMutation({
    mutationFn: (input: { email: string; code: string }) =>
      orpcPublicClient.onboarding.signup.verify(input),
    onSuccess: (result) => {
      if (result.existing || !result.key) {
        setExisting({ next: result.next, role: null, portalHome: null, ...result.existing });
        return;
      }
      signIn.mutate({ key: result.key, next: result.next });
    },
    onError: (error: Error) => {
      setProblem(error.message || "That code wasn't accepted. Check it, or send a new code.");
      codeRef.current?.focus();
    },
  });

  const onStart = (event: FormEvent) => {
    event.preventDefault();
    const parsed = startSignupInput.safeParse({ email, __hp });
    if (!parsed.success) {
      setProblem("Enter your work email address, like name@yourcommunity.com.");
      emailRef.current?.focus();
      return;
    }
    start.mutate(parsed.data.email);
  };

  const onVerify = (event: FormEvent) => {
    event.preventDefault();
    if (!sentTo) return;
    if (code.length !== 6) {
      setProblem("Enter all 6 digits from the email.");
      codeRef.current?.focus();
      return;
    }
    setProblem(null);
    verify.mutate({ email: sentTo, code });
  };

  const pending = start.isPending || verify.isPending || signIn.isPending;
  const startOver = () => {
    setExisting(null);
    setSentTo(null);
    setCode("");
    setProblem(null);
  };

  const problemLine = (
    <p id="signup-problem" role="alert" className="text-destructive text-sm empty:hidden">
      {problem}
    </p>
  );
  const invalid = problem ? { "aria-invalid": true, "aria-describedby": "signup-problem" } : {};

  return (
    <AuthFrame
      className="max-w-md"
      footer={
        <>
          By continuing you agree to the{" "}
          <Link
            to="/legal/subscription-agreement"
            className="-my-1 inline-block py-1 underline underline-offset-4"
          >
            subscription agreement
          </Link>
          .{" "}
          <a href="/security" className="-my-1 inline-block py-1 underline underline-offset-4">
            How we handle your data
          </a>
        </>
      }
    >
      <CardHeader className="items-center text-center">
        <CardTitle asChild className="text-xl">
          <h1>
            {existing
              ? "This email already has a login"
              : sentTo
                ? "Check your email"
                : "Create your account"}
          </h1>
        </CardTitle>
        <CardDescription>
          {existing
            ? "Your code was accepted. Choose what to do next."
            : sentTo
              ? `A 6-digit code is on its way to ${sentTo}. Enter it below to continue.`
              : "Start with your work email. A 6-digit code confirms it."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {existing && sentTo ? (
          <>
            <ExistingAccountPanel
              email={sentTo}
              role={existing.role}
              portalHome={existing.portalHome}
              pending={pending}
              onSignIn={() =>
                void navigate({ to: "/login", search: { email: sentTo, next: existing.next } })
              }
              onDifferentEmail={startOver}
            />
            <div className="mt-3">{problemLine}</div>
          </>
        ) : sentTo ? (
          <form className="space-y-4" onSubmit={onVerify} data-testid="form-signup-code">
            <div className="space-y-2">
              <Label htmlFor="signup-code">6-digit code</Label>
              <Input
                ref={codeRef}
                id="signup-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                enterKeyHint="go"
                placeholder="000000"
                maxLength={6}
                className="text-center text-lg tracking-[0.4em]"
                value={code}
                onChange={(event) => setCode(event.target.value.replaceAll(/\D/g, "").slice(0, 6))}
                required
                {...invalid}
              />
            </div>
            {problemLine}
            <Button className="w-full" disabled={pending} type="submit">
              {verify.isPending || signIn.isPending ? <Spinner /> : null}
              {verify.isPending ? "Checking…" : "Continue"}
            </Button>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <Button
                type="button"
                variant="link"
                className="min-h-6 p-0"
                disabled={pending}
                onClick={() => start.mutate(sentTo)}
              >
                {start.isPending ? "Sending…" : "Send a new code"}
              </Button>
              <Button type="button" variant="link" className="min-h-6 p-0" onClick={startOver}>
                Use a different email
              </Button>
            </div>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={onStart} data-testid="form-signup-email">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute left-[-9999px] top-[-9999px] size-0 overflow-hidden"
            >
              <label htmlFor="signup-hp">Website</label>
              <input
                id="signup-hp"
                name="__hp"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={__hp}
                onChange={(event) => setHp(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-email">Work email</Label>
              <Input
                ref={emailRef}
                id="signup-email"
                type="email"
                autoComplete="email"
                enterKeyHint="send"
                placeholder="name@yourcommunity.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                aria-invalid={problem ? true : undefined}
                aria-describedby={
                  problem ? "signup-email-hint signup-problem" : "signup-email-hint"
                }
              />
              <p id="signup-email-hint" className="text-muted-foreground text-xs">
                Your team can join you faster when you all use the same work domain.
              </p>
            </div>
            {problemLine}
            <Button className="w-full" disabled={pending} type="submit">
              {start.isPending ? <Spinner /> : null}
              {start.isPending ? "Sending…" : "Continue"}
            </Button>
            <p className="text-muted-foreground text-center text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-foreground underline underline-offset-4">
                Sign in
              </Link>
            </p>
          </form>
        )}
      </CardContent>
    </AuthFrame>
  );
}
